import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>

        {/* Terms & Conditions Content */}
        <div className="glass rounded-2xl p-8">
          <h1 className="text-4xl font-bold text-white mb-8 serif-display">Terms & Conditions</h1>
          
          <div className="prose prose-lg max-w-none text-gray-300 space-y-8">
            
            <div>
              <p className="text-gray-300 leading-relaxed mb-6">
                These Terms & Conditions ("Terms") govern your use of CoerciveControlPatterns.com ("we," "us," or "the Service"). 
                By purchasing or using our analysis services, you agree to these Terms.
              </p>
              
              <div className="bg-slate-800/50 rounded-lg p-6 mb-6">
                <ul className="space-y-2 text-gray-300">
                  <li>• Our platform provides AI-generated communication pattern analysis for informational purposes only.</li>
                  <li>• Reports are not legal advice, therapy, or evidence and should not be relied upon as such.</li>
                  <li>• All payments are final and non-refundable, except in cases of technical failure.</li>
                  <li>• We may restrict access, terminate accounts, or refuse service at our discretion.</li>
                  <li>• All disputes are governed by Wyoming, USA law, regardless of where you reside.</li>
                </ul>
              </div>
              
              <p className="text-amber-300 font-semibold mb-8">
                Please read these Terms carefully before using our Service.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">1. Definitions</h2>
              <ul className="space-y-2 text-gray-300">
                <li>• <strong>"Service"</strong> means the CoerciveControlPatterns.com website, platform, software, reports, and all related features.</li>
                <li>• <strong>"Customer"</strong> means any person or entity who accesses, uses, or purchases the Service.</li>
                <li>• <strong>"Data"</strong> means any files, communications, or information submitted by the Customer.</li>
                <li>• <strong>"Report"</strong> means the analysis results generated by our AI tools.</li>
                <li>• <strong>"Content"</strong> means all materials on the Service, including text, software, graphics, and reports.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">2. Acceptance of Terms</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                By accessing or using the Service, you agree to be bound by these Terms and all applicable laws. 
                If you do not agree, do not use the Service.
              </p>
              <p className="text-gray-300 leading-relaxed">
                We reserve the right to modify these Terms at any time. Updates will be posted here with a new "Last Updated" date. 
                Continued use after updates constitutes acceptance of the new Terms.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">3. Nature of the Service</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                CoerciveControlPatterns.com provides AI-generated analysis of communication patterns to help customers 
                recognize potential coercive or controlling behaviors.
              </p>
              <ul className="space-y-2 text-gray-300">
                <li>• The Service is for informational and educational purposes only.</li>
                <li>• We do not verify or authenticate the accuracy, completeness, or truthfulness of uploaded data or generated reports.</li>
                <li>• Reports are not legal advice, mental health treatment, evidence, or expert testimony.</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mt-4">
                Customers are solely responsible for how they interpret, use, or share their reports.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">4. Non-Therapy / Non-Diagnostic Disclaimer</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We are not a therapy provider or healthcare service. The Service does not diagnose, treat, or offer mental health advice. 
                Reports are not a substitute for professional counseling, therapy, or legal representation.
              </p>
              <p className="text-amber-300 font-semibold">
                If you are experiencing abuse or emotional distress, seek help from qualified professionals or local support services.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">5. No Legal Advice or Representation</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Nothing provided by CoerciveControlPatterns.com should be interpreted as legal advice. Reports are not legal documents, 
                and we do not act as attorneys, advocates, or expert witnesses.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Customers should always consult a licensed attorney before using any report or analysis in legal matters.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">6. Prohibited Uses</h2>
              <p className="text-gray-300 leading-relaxed mb-4">You agree not to use the Service to:</p>
              <ul className="space-y-2 text-gray-300">
                <li>• Create, distribute, or present reports as certified evidence in legal proceedings.</li>
                <li>• Misrepresent reports as professionally verified or legally binding.</li>
                <li>• Harass, surveil, stalk, or target individuals.</li>
                <li>• Upload unlawful, defamatory, or harmful content.</li>
                <li>• Attempt to reverse-engineer or misuse the Service.</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mt-4">
                We reserve the right to suspend or terminate access for violations of these Terms.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">7. Payments and Refund Policy</h2>
              <ul className="space-y-2 text-gray-300">
                <li>• All services are paid and billed at the time of purchase.</li>
                <li>• Payments are processed securely through third-party providers (e.g., Stripe).</li>
                <li>• Refunds are only available in cases of technical failure — meaning the Service fails to generate a report after valid payment.</li>
                <li>• Refunds will not be issued for dissatisfaction with results, interpretation, or use of the report.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">8. Taxes</h2>
              <p className="text-gray-300 leading-relaxed">
                Sales tax, VAT, or other applicable taxes will be calculated and collected where required by law. 
                Customers are responsible for any additional taxes or reporting obligations in their jurisdiction.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">9. Intellectual Property</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                All software, algorithms, trademarks, logos, and materials on the Service are the exclusive property of CoerciveControlPatterns.com.
              </p>
              <ul className="space-y-2 text-gray-300">
                <li>• Customers receive a limited, non-exclusive license to use reports for personal or organizational purposes.</li>
                <li>• You may not reproduce, distribute, resell, or publicly display reports without written permission.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">10. Data Handling and Privacy</h2>
              <ul className="space-y-2 text-gray-300">
                <li>• All uploaded data and generated reports are automatically deleted within 24 hours, unless downloaded earlier.</li>
                <li>• We do not store, access, or share your data beyond what is necessary to deliver the Service.</li>
                <li>• By using the Service, you agree to our Privacy Policy.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">11. Limitation of Liability</h2>
              <p className="text-gray-300 leading-relaxed mb-4">To the fullest extent permitted by law:</p>
              <ul className="space-y-2 text-gray-300">
                <li>• We are not liable for any damages, losses, or claims resulting from your use or inability to use the Service.</li>
                <li>• We make no warranties regarding the accuracy, completeness, or usefulness of any report.</li>
                <li>• You are solely responsible for decisions or actions you take based on the information provided.</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mt-4">
                In no event shall our total liability exceed the amount you paid for the Service.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">12. Indemnification</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                You agree to indemnify and hold harmless CoerciveControlPatterns.com, its owners, employees, and affiliates from any claims, 
                damages, or liabilities arising from:
              </p>
              <ul className="space-y-2 text-gray-300">
                <li>• Your use of the Service.</li>
                <li>• Your violation of these Terms.</li>
                <li>• Your misuse or misrepresentation of any report.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">13. Jurisdiction and Governing Law</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                These Terms are governed by and construed in accordance with the laws of the State of Wyoming, United States, 
                without regard to conflict-of-law principles.
              </p>
              <p className="text-gray-300 leading-relaxed">
                All disputes shall be resolved exclusively in the state or federal courts located in Wyoming.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">14. Global Use and Jurisdictional Limits</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We provide global access but may block access from jurisdictions where our Service would violate local law.
              </p>
              <p className="text-gray-300 leading-relaxed">
                By using the Service, you consent to the transfer and processing of your data in the United States.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">15. Termination</h2>
              <p className="text-gray-300 leading-relaxed">
                We may suspend or terminate your access to the Service at any time for any reason, including violations of these Terms. 
                Termination does not affect any accrued rights or obligations.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">16. Contact Information</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                For questions about these Terms or the Service, contact us at:
              </p>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-gray-300"><strong>Email:</strong> support@coercivecontrolpatterns.com</p>
                <p className="text-gray-300"><strong>Jurisdiction:</strong> State of Wyoming, United States</p>
              </div>
            </div>

            <div className="text-center mt-12 pt-8 border-t border-slate-700">
              <p className="text-gray-400 text-sm">
                Last Updated: {new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;